// ===== Roster List (Sidebar) =====
// T2 Phase 2: "팀원" 섹션 컨테이너(#rosterList)를 known actor 목록으로 채운다.
// 팀원 행 단일클릭 → 상대와 1:1 DM 즉시 생성/열기 (POST /api/rooms). 초대 단계 없음.

interface RosterActor {
  actorId: string;
  displayName: string;
  avatarUrl?: string | null;
  isSelf?: boolean;
}

interface RosterRoom {
  id: string;
  sessionId?: string;
}

function isRosterRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isRosterActor(value: unknown): value is RosterActor {
  return isRosterRecord(value) && typeof value.actorId === "string" && typeof value.displayName === "string"
    && (value.avatarUrl === undefined || value.avatarUrl === null || typeof value.avatarUrl === "string")
    && (value.isSelf === undefined || typeof value.isSelf === "boolean");
}

function readRosterRoom(value: unknown): RosterRoom | null {
  if (!isRosterRecord(value) || typeof value.id !== "string") return null;
  return { id: value.id, sessionId: typeof value.sessionId === "string" ? value.sessionId : undefined };
}

const RosterList = (() => {
  // 사람 아바타 폴백 색은 design-tokens.css --peer-avatar-* (슬레이트 6톤)이 소유. actorId 기준 결정적 배정.
  const PEER_AVATAR_COUNT = 6;

  function _initial(name: string): string {
    const trimmed = (name || "").trim();
    return trimmed ? (trimmed[0]?.toUpperCase() ?? "?") : "?";
  }

  function _avatarColor(seed: string): string {
    const s = String(seed || "");
    let h = 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return `var(--peer-avatar-${h % PEER_AVATAR_COUNT})`;
  }

  function _renderAvatar(actor: RosterActor): HTMLSpanElement {
    const avatar = document.createElement("span");
    avatar.className = "roster-avatar";
    avatar.style.background = _avatarColor(actor.actorId || actor.displayName);
    avatar.textContent = _initial(actor.displayName);

    // Google 프로필 사진이 있으면 이니셜 위에 덮어 그린다. 로드 실패 시 img를 걷어내
    // 이니셜 폴백으로 되돌린다.
    if (actor.avatarUrl) {
      const img = document.createElement("img");
      img.className = "roster-avatar-img";
      img.src = actor.avatarUrl;
      img.alt = "";
      img.referrerPolicy = "no-referrer";
      img.addEventListener("error", () => img.remove());
      avatar.appendChild(img);
    }
    return avatar;
  }

  // 상대 팀원 단일클릭 → 1:1 DM 즉시 생성(재사용) 후 방 열기(D02 카톡식 단일클릭).
  // 서버가 인증/멤버 확정을 담당하므로 프론트는 실호출 결과만 소비한다.
  async function _openDmWith(actorId: string): Promise<void> {
    try {
      const res = await fetch("/api/rooms", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ roomType: "dm", memberActorIds: [actorId] }),
      });
      if (!res.ok) return;
      const data: unknown = await res.json();
      if (!isRosterRecord(data) || data.ok !== true) return;
      const room = readRosterRoom(data.room);
      if (!room) return;
      if (typeof RoomList !== "undefined") RoomList.refresh();
      if (wsClient?.isConnected && room.sessionId) {
        wsClient.resumeSession(room.sessionId, { roomId: room.id });
      }
    } catch (err) {
      console.error("[roster-list] DM 열기 실패:", err);
    }
  }

  function _renderActorRow(actor: RosterActor): HTMLDivElement {
    const row = document.createElement("div");
    row.className = "roster-item";
    row.dataset.actorId = actor.actorId;

    const name = document.createElement("span");
    name.className = "roster-name";
    name.textContent = actor.displayName;
    if (actor.isSelf) {
      const tag = document.createElement("span");
      tag.className = "roster-self-tag";
      tag.textContent = " (나)";
      name.appendChild(tag);

      // '나' 행 클릭 → 내 프로필(이름+사진) 편집 모달(P4).
      row.classList.add("roster-item--actionable");
      row.title = "클릭하여 프로필 편집";
      row.addEventListener("click", () => {
        if (typeof ProfileEdit !== "undefined") ProfileEdit.open(actor);
      });
    } else {
      // 나 자신과는 DM을 열 수 없다 — 상대 행만 단일클릭 진입점으로 만든다(D02).
      row.classList.add("roster-item--actionable");
      row.title = "클릭하여 대화 시작";
      row.addEventListener("click", () => _openDmWith(actor.actorId));
    }

    row.appendChild(_renderAvatar(actor));
    row.appendChild(name);
    return row;
  }

  function _updateCount(n: number): void {
    const el = document.getElementById("rosterCount");
    if (el) el.textContent = n ? `· ${n}` : "";
  }

  function _render(users: RosterActor[]): void {
    const list = document.getElementById("rosterList");
    if (!list) return;

    // 로스터 = 사람만. system:* 등 비인간 액터는 제외한다.
    const people = (users || []).filter((u) => !String(u.actorId || "").startsWith("system:"));

    // '나'(실제 self actor 바인딩, isSelf 플래그)를 최상단으로 고정한다(CAP10).
    // Array#sort는 stable이므로 나머지 팀원의 상대 순서는 그대로 유지된다.
    people.sort((a, b) => (b.isSelf ? 1 : 0) - (a.isSelf ? 1 : 0));

    list.innerHTML = "";
    _updateCount(people.length);

    if (people.length === 0) {
      const empty = document.createElement("div");
      empty.className = "session-empty";
      empty.textContent = "아직 팀원이 없어요";
      list.appendChild(empty);
      return;
    }

    people.forEach((actor) => list.appendChild(_renderActorRow(actor)));
  }

  async function refresh() {
    try {
      const res = await fetch("/api/rooms/actors");
      if (!res.ok) {
        _render([]);
        return;
      }
      const data: unknown = await res.json();
      const users = isRosterRecord(data) && data.ok === true && Array.isArray(data.users)
        ? data.users.filter(isRosterActor)
        : [];
      _render(users);
    } catch (err) {
      console.error("[roster-list] fetch failed:", err);
      _render([]);
    }
  }

  function init() {
    refresh();
  }

  return { init, refresh };
})();

document.addEventListener("DOMContentLoaded", () => {
  RosterList.init();
});
